// TanStack Query keys + REST fetchers (axios -> FastAPI).
import { phaseNames } from './steps';
import { api } from './api';

export interface Client {
  id: string;
  name: string;
  logo_url?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  vertical: string;
  taxonomy: {
    primary: { label: string; values: string[] };
    secondary: { label: string; values: string[] };
    talent: { label: string; enabled: boolean };
  };
  talents: Array<{ id: string; name: string; photo_url?: string | null; telegram_linked?: boolean; portal_user_id?: string | null; portal_email?: string | null;
    /** onboarding (migration 053): typed by the team; used once the doctor confirms */
    phone?: string | null; email?: string | null; whatsapp_linked?: boolean; whatsapp_hint?: string | null; email_confirmed?: boolean }>;
  default_channels: string[];
  active: boolean;
  client_adjustment_limit: number;
  created_at: string;
  created_by: string;
}

export interface FunnelMix { TOF: number; MOF: number; BOF: number }

export interface CreativeSlot {
  id: string;
  funnel: 'TOF' | 'MOF' | 'BOF';
  type: string;
  working_title?: string | null;
  /** added for an extra creative after the plan, not planned (api strategy_slots.py) */
  extra?: boolean;
}

export interface Strategy {
  id: string;
  code: string;
  client_id: string;
  client_name: string;
  title: string;
  description?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  funnel_mix: FunnelMix;
  creative_mix: Record<string, number>;
  slots: CreativeSlot[];
  status: 'draft' | 'active' | 'done' | 'archived';
  strategist_id: string;
  strategist_name: string;
  created_at: string;
  talent_id?: string | null;
}

export interface Creative {
  deferred_until?: string | null; deferred_reason?: string | null; deferred_at?: string | null;
  skipped_phases?: string[];
  id: string;
  code: string;
  sequence: number;
  strategy_id: string;
  strategy_code: string;
  slot_id?: string | null;
  /** made from a client request (requests v2): the request and its conversation are the source of the brief */
  client_request_id?: string | null;
  client_id: string;
  client_name: string;
  title: string;
  description?: string | null;
  script?: string | null;
  /** formatted copies (docs/RICH-TEXT.md); the plain fields above stay for everything else */
  description_html?: string | null;
  script_html?: string | null;
  creative_type: 'video' | 'reel' | 'static' | 'carousel' | 'story' | 'photo' | 'copy';
  categories: string[];
  funnel_stage: 'TOF' | 'MOF' | 'BOF';
  is_paid: boolean;
  needs_shoot?: boolean;      // migration 038: off = made from existing material
  ad_platforms: string[];
  taxonomy_primary?: string | null;
  taxonomy_secondary?: string | null;
  talent_id?: string | null;
  talent_name?: string | null;
  /** How the person on camera wants their own work handled, from the client's talent entry. */
  talent_note?: string | null;
  channels: string[];
  current_stage: 'brief' | 'shoot' | 'raws_review' | 'edit' | 'dual_review' | 'done' | 'posted';
  /** The finer step the creative is on ('rough', 'ad_raws'), from lib/steps.ts. */
  step_key?: string | null;
  step_label?: string | null;
  status: 'planned' | 'in_progress' | 'blocked' | 'done';
  revision_round: number;
  raw_folders?: string[];
  shoot_at?: string | null;
  shoot_location?: string | null;
  script_status?: 'draft' | 'sent' | 'approved' | 'changes_requested';
  script_sent_at?: string | null;
  script_decided_at?: string | null;
  script_feedback?: string | null;
  stage_entered_at?: string | null;
  stage_due_at?: string | null;
  due_extended_until?: string | null;
  due_extension_note?: string | null;
  client_visible: boolean;
  client_adjustments_used: number;
  client_adjustments_extra: number;
  client_approved_at?: string | null;
  client_approved_by?: string | null;
  state: 'Draft' | 'Upload' | 'Approved' | 'Posted';
  execution_date?: string | null;
  posting_date?: string | null;
  posted_url?: string | null;
  posted_links?: PostedLink[];
  reference_urls?: string[];
  credits: Record<string, { id: string; name: string }>;
  latest_asset_thumbnail_path?: string | null;
  latest_asset_thumbnail_url?: string | null;
  latest_asset_storage_path?: string | null;
  latest_asset_content_type?: string | null;
  latest_asset_url?: string | null;
  latest_asset_at?: string | null;
  asset_count: number;
  last_activity_at: string;
  created_at: string;
  created_by: string;
}

export interface ActivityRow {
  id: string;
  actor_id: string;
  actor_name: string;
  verb: string;
  target_type: string;
  target_id: string;
  target_title?: string | null;
  // Two shapes coexist in this column:
  //   field-diff style: { field: { old, new } }  (used by patches that track per-field changes)
  //   plain-info style: { key: value }            (used by ad-hoc activity like uploads, reviews, etc.)
  meta?: Record<string, unknown> | null;
  client_id?: string | null;
  strategy_id?: string | null;
  creative_id?: string | null;
  at: string;
}

export const qk = {
  me:                                 ['me'] as const,
  myScopes:                           ['my-scopes'] as const,
  clientTeam: (clientId: string)      => ['client-team', clientId] as const,
  // client documents vault (lib/documents.ts)
  docCatalog:                         ['doc-catalog'] as const,
  clientDocuments: (clientId: string) => ['client-documents', clientId] as const,
  portalDocuments:                    ['portal-documents'] as const,
  // brand kit designs (lib/brand-kit.ts): base + mine + shared, and the portal's shared designs
  brandTemplates: (clientId: string)  => ['brand-templates', clientId] as const,
  brandVideo: (clientId: string)      => ['brand-video', clientId] as const,
  brandVideoRenders: (clientId: string) => ['brand-video-renders', clientId] as const,
  portalDesigns:                      ['portal-designs'] as const,
  clientContacts: (clientId: string)  => ['client-contacts', clientId] as const,
  clientLocations: (clientId: string) => ['client-locations', clientId] as const,
  taskCandidates: (creativeId: string, designation: string) => ['task-candidates', creativeId, designation] as const,
  portalApproval: (creativeId: string) => ['portal-approval', creativeId] as const,
  clients:                            ['clients'] as const,
  client:  (id: string)               => ['clients', id] as const,
  strategies:                         ['strategies'] as const,
  strategy: (id: string)              => ['strategies', id] as const,
  strategiesByClient: (clientId: string) => ['strategies', 'byClient', clientId] as const,
  strategyActivity: (id: string)      => ['strategies', id, 'activity'] as const,
  creatives:                          ['creatives'] as const,
  creativesFiltered: (params: Record<string, string | undefined>) => ['creatives', params] as const,
  creative:  (id: string)             => ['creatives', id] as const,
  creativeActivity: (id: string)      => ['creatives', id, 'activity'] as const,
};

export async function fetchClients(): Promise<Client[]> {
  return (await api.get<Client[]>('/clients')).data;
}
export async function fetchClient(id: string): Promise<Client> {
  return (await api.get<Client>(`/clients/${id}`)).data;
}
export async function createClient(payload: Partial<Client>): Promise<Client> {
  return (await api.post<Client>('/clients', payload)).data;
}
export interface TaxonomyPatchBody {
  primary_label?: string;
  primary_values?: string[];
  secondary_label?: string;
  secondary_values?: string[];
  talent_label?: string;
  talent_enabled?: boolean;
  talents?: Array<{ id?: string; name: string; primary_tag_id?: string; photo_url?: string; custom?: Record<string, string> }>;
  default_channels?: string[];
}
export async function patchTaxonomy(clientId: string, body: TaxonomyPatchBody): Promise<Client> {
  return (await api.patch<Client>(`/clients/${clientId}/taxonomy`, body)).data;
}
export interface TaxonomyImportResult {
  primary_values: string[];
  secondary_values: string[];
  talents: Array<{ id: string; name: string; primary_tag_id?: string | null; photo_url?: string | null; custom?: Record<string, string> }>;
  warnings: string[];
}
export async function importTaxonomy(clientId: string, file: File, apply: boolean): Promise<TaxonomyImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  return (await api.post<TaxonomyImportResult>(
    `/clients/${clientId}/taxonomy/import?apply=${apply}`,
    fd,
  )).data;
}
export function taxonomyTemplateUrl(clientId: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || '/api';
  return `${base}/clients/${clientId}/taxonomy/template.xlsx`;
}

export async function fetchStrategies(): Promise<Strategy[]> {
  return (await api.get<Strategy[]>('/strategies')).data;
}
export async function fetchStrategiesByClient(clientId: string): Promise<Strategy[]> {
  return (await api.get<Strategy[]>('/strategies', { params: { client_id: clientId } })).data;
}
export async function fetchStrategy(id: string): Promise<Strategy> {
  return (await api.get<Strategy>(`/strategies/${id}`)).data;
}
export interface StrategyCreateBody {
  client_id: string;
  title: string;
  description?: string;
  period_start?: string;
  period_end?: string;
  funnel_mix: FunnelMix;
  creative_mix: Record<string, number>;
  talent_id?: string;
}
/** The doctor a strategy is for, with their profile (migration 061). */
export interface StrategyDoctor {
  talent_id: string; name: string; photo_url?: string | null; speciality?: string | null;
  fields: Array<{ label: string; value: string }>; production_notes?: string | null; profile?: string | null; can_edit: boolean;
}
export async function fetchStrategyDoctor(id: string): Promise<StrategyDoctor | null> {
  return (await api.get<StrategyDoctor | null>(`/strategies/${id}/doctor`)).data;
}
export async function pickStrategyDoctor(id: string, talentId: string | null): Promise<StrategyDoctor | null> {
  return (await api.put<StrategyDoctor | null>(`/strategies/${id}/doctor`, { talent_id: talentId })).data;
}
export async function saveDoctorProfile(id: string, profile: string): Promise<StrategyDoctor> {
  return (await api.put<StrategyDoctor>(`/strategies/${id}/doctor/profile`, { profile })).data;
}
export async function createStrategy(body: StrategyCreateBody): Promise<Strategy> {
  return (await api.post<Strategy>('/strategies', body)).data;
}

// ---- creatives ----
export async function fetchCreatives(params: Record<string, string | undefined> = {}): Promise<Creative[]> {
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) if (v) clean[k] = v;
  return (await api.get<Creative[]>('/creatives', { params: clean })).data;
}
export async function fetchCreative(id: string): Promise<Creative> {
  return (await api.get<Creative>(`/creatives/${id}`)).data;
}
export interface CreativeCreateBody {
  strategy_id: string;
  slot_id?: string;
  title: string;
  description?: string;
  script?: string;
  description_html?: string;
  script_html?: string;
  creative_type: Creative['creative_type'];
  funnel_stage: Creative['funnel_stage'];
  categories?: string[];
  is_paid?: boolean;
  needs_shoot?: boolean;
  ad_platforms?: string[];
  taxonomy_primary?: string;
  taxonomy_secondary?: string;
  talent_id?: string;
  talent_name?: string;
  channels?: string[];
  execution_date?: string;
  posting_date?: string;
  reference_urls?: string[];
  script_needs_doctor?: boolean;
  shoot_notes?: string;
  submit?: boolean;          // false (default) = save as draft, true = handed off to production
}
export async function createCreative(body: CreativeCreateBody): Promise<Creative> {
  return (await api.post<Creative>('/creatives', body)).data;
}
export async function patchCreative(id: string, body: Partial<Creative>): Promise<Creative> {
  return (await api.patch<Creative>(`/creatives/${id}`, body)).data;
}
/** Who may change the brief right now (api/app/edit_lock.py). */
export interface EditLock {
  locked: boolean;
  locked_at?: string | null;
  locked_by?: string | null;
  message: string;
  /** may edit with no reason */
  editable: boolean;
  /** owner or manager past the brief: may edit, with a reason */
  override: boolean;
  /** the SMM at the posting step: the posting date only */
  posting_date_only: boolean;
  is_owner: boolean;
  can_send_back: boolean;
  owners: string[];
  execution_date_from_shoot: boolean;
}
export async function fetchEditLock(id: string): Promise<EditLock> {
  return (await api.get<EditLock>(`/creatives/${id}/edit-lock`)).data;
}
export async function sendBackToBrief(id: string, reason: string): Promise<EditLock> {
  return (await api.post<EditLock>(`/creatives/${id}/send-back-to-brief`, { reason })).data;
}
/** Only the fields that changed, what the editor saw of them, and a reason past the brief. */
export async function editCreative(id: string, body: Record<string, unknown> & { reason?: string; expected?: Record<string, unknown> }): Promise<Creative> {
  return (await api.patch<Creative>(`/creatives/${id}`, body)).data;
}
export async function fetchCreativeActivity(id: string): Promise<ActivityRow[]> {
  return (await api.get<ActivityRow[]>(`/creatives/${id}/activity`)).data;
}
export async function fetchStrategyActivity(id: string): Promise<ActivityRow[]> {
  return (await api.get<ActivityRow[]>(`/strategies/${id}/activity`)).data;
}

// ---- approvals ----
export type ApproverRole = 'art_director_raws' | 'art_director_dual' | 'content_creator_dual' | 'client';
export type ApprovalDecision = 'pending' | 'approved' | 'revision_requested' | 'skipped';

export interface Approval {
  id: string;
  creative_id: string;
  approver_role: ApproverRole;
  approver_id?: string | null;
  approver_name?: string | null;
  revision_round: number;
  decision: ApprovalDecision;
  notes?: string | null;
  requested_by?: string | null;
  requested_at: string;
  decided_at?: string | null;
  approver_ids?: string[];
  approver_names?: string[];
}

export async function fetchApprovals(creativeId: string): Promise<Approval[]> {
  return (await api.get<Approval[]>(`/creatives/${creativeId}/approvals`)).data;
}
export async function requestApproval(creativeId: string, role: ApproverRole, approverIds?: string[]): Promise<Approval> {
  return (await api.post<Approval>(`/creatives/${creativeId}/approvals`, { approver_role: role, approver_ids: approverIds ?? null })).data;
}
export interface ClientContact { id: string; display_name: string; email: string; last_seen_at?: string | null; invited_at?: string | null; can_upload_material?: boolean; portal_scope?: 'full' | 'doctor' | 'uploads'; portal_talent_id?: string | null; portal_talent_name?: string | null; portal_talent_ids?: string[]; portal_talent_names?: string[]; whatsapp_linked?: boolean; whatsapp_hint?: string | null; email_confirmed?: boolean }
export async function fetchClientContacts(clientId: string): Promise<ClientContact[]> {
  return (await api.get<ClientContact[]>(`/clients/${clientId}/contacts`)).data;
}
export interface InviteResult { user: { id: string; email: string; display_name: string }; invite_url: string; expires_in_days: number }
export async function reissueInvite(userId: string): Promise<InviteResult> {
  return (await api.post<InviteResult>(`/users/${userId}/reissue-invite`)).data;
}
export async function inviteClientContact(clientId: string, email: string, displayName: string): Promise<InviteResult> {
  return (await api.post<InviteResult>('/users', { email, display_name: displayName, role: 'client', designations: [], client_id: clientId })).data;
}
export async function decideApproval(approvalId: string, decision: 'approved' | 'revision_requested', notes?: string, mentions: string[] = []): Promise<Approval> {
  return (await api.post<Approval>(`/approvals/${approvalId}/decide`, { decision, notes, mentions })).data;
}
/** One button: approve or reject whatever the current stage is waiting on. */
export async function quickDecide(creativeId: string, decision: 'approved' | 'rejected', notes?: string, overrideReason?: string, mentions: string[] = [], backTo?: 'rough' | 'colour' | 'final', finalAssetIds?: string[]): Promise<{ creative_id: string; stage: string; revision_round: number; decided_roles: string[]; decision: string }> {
  return (await api.post(`/creatives/${creativeId}/decide`, { decision, notes, override_reason: overrideReason, mentions, back_to: backTo,
    final_asset_ids: finalAssetIds ?? null })).data;
}
/** The clinic's folders: steps with counts, then the creatives in one step. */
export interface DriveOut { folders: Array<{ key: string; label: string; count: number }>; uploads: number }
export interface DriveCreative { id: string; code: string; title: string; creative_type: string; funnel_stage?: string | null; files: Record<string, number>; last_activity_at?: string | null }
export async function fetchDrive(clientId: string): Promise<DriveOut> {
  return (await api.get<DriveOut>(`/clients/${clientId}/drive`)).data;
}
export async function fetchDriveFolder(clientId: string, folder: string): Promise<DriveCreative[]> {
  return (await api.get<DriveCreative[]>(`/clients/${clientId}/drive/${folder}`)).data;
}

/** Files on the clinic's other creatives, and copying them onto this one. */
export interface OtherAsset { asset: MediaAsset; creative_id: string; creative_code: string; creative_title: string }
export async function fetchOtherAssets(creativeId: string): Promise<OtherAsset[]> {
  return (await api.get<OtherAsset[]>(`/creatives/${creativeId}/other-assets`)).data;
}
export async function copyAssets(creativeId: string, assetIds: string[]): Promise<MediaAsset[]> {
  return (await api.post<MediaAsset[]>(`/creatives/${creativeId}/copy-assets`, { asset_ids: assetIds })).data;
}

/** Work done outside the Warehouse: move on to the step it has really reached (owners and managers). */
export async function doneOutside(creativeId: string, reached: string, note: string): Promise<Creative> {
  return (await api.post<Creative>(`/creatives/${creativeId}/done-outside`, { reached, note })).data;
}

export async function extendDue(creativeId: string, until: string, note: string): Promise<Creative> {
  return (await api.post<Creative>(`/creatives/${creativeId}/extend-due`, { until, note })).data;
}

// ---- comments ----
export interface Comment {
  id: string;
  thread?: string;
  attachments?: ChatAttachment[];
  creative_id?: string | null;
  client_id?: string | null;
  author_id: string;
  author_name: string;
  body: string;
  audio_url?: string | null;
  mentioned_user_ids?: string[];
  refs?: CommentRef[];
  visibility: 'internal' | 'client';
  created_at: string;
  edited_at?: string | null;
}
export async function fetchComments(creativeId: string): Promise<Comment[]> {
  return (await api.get<Comment[]>(`/creatives/${creativeId}/comments`)).data;
}
export interface CommentRef { type: 'creative' | 'strategy' | 'task' | 'asset'; id: string; label: string; link: string }
export async function postComment(creativeId: string, body: string, visibility: 'internal' | 'client' = 'internal', audioPath?: string | null, mentionedUserIds: string[] = [], refs: CommentRef[] = []): Promise<Comment> {
  return (await api.post<Comment>(`/creatives/${creativeId}/comments`, { body, visibility, audio_path: audioPath ?? null, mentioned_user_ids: mentionedUserIds, refs })).data;
}
export async function markChatRead(creativeId: string): Promise<void> {
  await api.post(`/creatives/${creativeId}/comments/read`);
}
export type ChatKind = 'creative' | 'client' | 'company' | 'shoot' | 'clinic' | 'request';
export interface ChatSummary { kind: ChatKind; thread: string; creative_id?: string | null; shoot_id?: string | null; client_id?: string | null; code?: string | null; title: string; client_name: string; last_at?: string | null; last_author?: string | null; last_body?: string | null; unread: number }
export async function fetchThreadMessages(kind: ChatKind, id: string): Promise<Comment[]> {
  return (await api.get<Comment[]>(`/chat/${kind}/${id}/messages`)).data;
}
export interface ChatAttachment { path: string; name: string; content_type: string; size: number; url?: string | null }
export async function postThreadMessage(kind: ChatKind, id: string, body: string, visibility: 'internal' | 'client' = 'internal', audioPath?: string | null, mentionedUserIds: string[] = [], refs: CommentRef[] = [], attachments: ChatAttachment[] = []): Promise<Comment> {
  return (await api.post<Comment>(`/chat/${kind}/${id}/messages`, { body, visibility, audio_path: audioPath ?? null, mentioned_user_ids: mentionedUserIds, refs, attachments: attachments.map((a) => ({ path: a.path, name: a.name, content_type: a.content_type, size: a.size })) })).data;
}
export async function getChatUploadUrl(body: { filename: string; content_type: string; size_bytes: number }): Promise<{ path: string; upload_url: string; resumable: boolean }> {
  return (await api.post('/chat/upload-url', body)).data;
}
export async function markThreadRead(kind: ChatKind, id: string): Promise<void> {
  await api.post(`/chat/${kind}/${id}/read`);
}
export async function fetchChats(): Promise<ChatSummary[]> {
  return (await api.get<ChatSummary[]>('/chats')).data;
}
export interface SearchHit { type: 'creative' | 'strategy' | 'task' | 'asset'; id: string; label: string; sub?: string | null; link: string }
export async function searchItems(q: string, creativeId?: string): Promise<SearchHit[]> {
  return (await api.get<SearchHit[]>('/search', { params: { q, creative_id: creativeId } })).data;
}

// ---- tasks ----
export interface Task {
  id: string;
  creative_id: string;
  creative_code: string;
  creative_title: string;
  client_id: string;
  strategy_id: string;
  stage: string;
  phase?: string | null;
  required_designation?: string | null;
  assignee_id?: string | null;
  assignee_name?: string | null;
  status: 'pending' | 'in_progress' | 'submitted' | 'done' | 'blocked' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date?: string | null;
  due_at?: string | null;
  notes?: string | null;
  assigned_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
}
/** A creative's files by stage and round (Aziz 2026-09-29). */
export interface StageRound {
  n: number; state: 'in_progress' | 'waiting' | 'approved' | 'changes' | 'replaced'; carried_from?: number | null; carried_label?: string | null; submitted_at?: string | null; submitted_by?: string | null;
  decided_at?: string | null; decided_by?: string | null; note?: string | null; asset_ids: string[];
}
export interface StageSection {
  key: string; label: string; state: string; rounds: StageRound[]; groups: { name: string; asset_ids: string[] }[]; text?: string | null; html?: string | null; count: number;
}
export async function fetchStages(creativeId: string): Promise<StageSection[]> {
  return (await api.get<StageSection[]>(`/creatives/${creativeId}/stages`)).data;
}

/** Job and clinics (Aziz 2026-09-29): what a person approves, set from their account. */
export interface JobSeats {
  user_id: string; display_name: string; job: string | null; job_label: string | null; seats: string[];
  clinics: { client_id: string; name: string; on_job: boolean; one_person: boolean; holders: Record<string, string[]> }[];
}
export async function fetchJobSeats(userId: string): Promise<JobSeats> {
  return (await api.get<JobSeats>(`/admin/users/${userId}/job-seats`)).data;
}
export async function saveJobSeats(userId: string, clientIds: string[], onePersonClientIds: string[]): Promise<JobSeats> {
  return (await api.put<JobSeats>(`/admin/users/${userId}/job-seats`, { client_ids: clientIds, one_person_client_ids: onePersonClientIds })).data;
}

/** Mohannad 2026-09-29: every review and cut waiting on my decision, oldest first. */
export interface WaitingItem {
  kind: 'review' | 'phase' | 'shoot'; link: string; creative_id: string; creative_code: string; creative_title: string; clinic: string;
  what: string; seat_label: string; asked_by?: string | null; since?: string | null; revision_round: number;
}
export async function fetchWaitingOnMe(): Promise<WaitingItem[]> {
  return (await api.get<WaitingItem[]>('/me/waiting-on-me')).data;
}

export async function fetchMyTasks(status?: string): Promise<Task[]> {
  return (await api.get<Task[]>('/tasks/my', { params: status ? { status } : {} })).data;
}
/** One place a piece went live (migration 076). */
export interface PostedLink { platform: string; url: string; at?: string; by?: string }

/** Where a piece can be posted: the same list as the server (creatives.py POST_PLACES). */
export const POST_PLACES: { value: string; label: string }[] = [
  { value: 'instagram_post', label: 'Instagram post' }, { value: 'instagram_reel', label: 'Instagram reel' },
  { value: 'instagram_story', label: 'Instagram story' }, { value: 'tiktok', label: 'TikTok' },
  { value: 'snapchat', label: 'Snapchat' }, { value: 'facebook', label: 'Facebook' }, { value: 'facebook_story', label: 'Facebook story' },
  { value: 'youtube', label: 'YouTube' }, { value: 'youtube_shorts', label: 'YouTube Shorts' }, { value: 'linkedin', label: 'LinkedIn' },
  { value: 'x_twitter', label: 'X' }, { value: 'threads', label: 'Threads' }, { value: 'pinterest', label: 'Pinterest' },
  { value: 'whatsapp_channel', label: 'WhatsApp channel' }, { value: 'telegram', label: 'Telegram' },
  { value: 'google_business', label: 'Google Business' }, { value: 'website', label: 'Website' }, { value: 'other', label: 'Other' },
];
export const postPlaceLabel = (v: string) => POST_PLACES.find((p) => p.value === v)?.label ?? v;

/** The posting step, done: every place it went live. The list sent is the whole list (adds, corrects, removes). */
export async function markPosted(creativeId: string, links: { platform: string; url: string }[]): Promise<Creative> {
  return (await api.post<Creative>(`/creatives/${creativeId}/posted`, { links })).data;
}
/** Why each edit phase cannot take an upload yet (null = open), from the server's gate. */
export async function fetchPhaseGate(creativeId: string): Promise<Record<string, string | null>> {
  return (await api.get<Record<string, string | null>>(`/creatives/${creativeId}/phase-gate`)).data;
}
export async function fetchCreativeTasks(creativeId: string): Promise<Task[]> {
  return (await api.get<Task[]>('/tasks', { params: { creative_id: creativeId } })).data;
}
export interface TaskCreateBody {
  creative_id: string;
  stage: string;
  phase?: string;
  required_designation?: string;
  assignee_id?: string;
  assignee_name?: string;
  priority?: Task['priority'];
  due_date?: string;
  notes?: string;
  auto_assign?: boolean;
}
export interface TaskCandidate { id: string; display_name: string; designations: string[]; open_tasks: number; urgent_tasks: number }
/** Who may take a task on this creative, least loaded first. Needs the assign permission. */
export async function fetchTaskCandidates(creativeId: string, designation?: string): Promise<TaskCandidate[]> {
  const params: Record<string, string> = { creative_id: creativeId };
  if (designation) params.designation = designation;
  return (await api.get<TaskCandidate[]>('/tasks/candidates', { params })).data;
}
export async function createTask(body: TaskCreateBody): Promise<Task> {
  return (await api.post<Task>('/tasks', body)).data;
}
export async function patchTask(id: string, body: Partial<Task>): Promise<Task> {
  return (await api.patch<Task>(`/tasks/${id}`, body)).data;
}
export interface TaskSubmitResult { task: Task; stage: string; requested_roles: string[]; notified: number }
/** Close the task, move the creative to its review stage, open the approvals, tell the reviewers. */
export async function submitTask(id: string): Promise<TaskSubmitResult> {
  return (await api.post<TaskSubmitResult>(`/tasks/${id}/submit`)).data;
}
export const SUBMIT_STAGES = ['shoot', 'edit'];
/** What a step is called, per creative type: the edit has phases. */
export function stepLabel(stage: string, phase?: string | null, creativeType?: string): string {
  if (!phase) return stage.replace(/_/g, ' ');
  return (phaseNames(creativeType) as Record<string, string>)[phase] ?? phase;
}
export function submitLabel(stage: string, phase?: string | null, creativeType?: string): string {
  if (stage === 'shoot') return 'Submit raws for AD review';
  if (phase && phase !== 'final') return `Submit the ${stepLabel(stage, phase, creativeType).toLowerCase()}`;
  return 'Submit for final approval';
}

// ---- calendar ----
export interface CalendarEvent {
  id: string;
  kind: 'creative_execution' | 'creative_posting' | 'task' | 'shoot';
  shoot_id?: string | null;
  time?: string | null;
  location?: string | null;
  crew_names?: string[];
  creative_count?: number;
  step_key?: string | null;
  step_label?: string | null;
  step_owner?: string | null;
  date: string;
  title: string;
  code?: string | null;
  creative_id?: string | null;
  creative_type?: string | null;
  funnel_stage?: 'TOF' | 'MOF' | 'BOF' | null;
  current_stage?: string | null;
  state?: string | null;
  is_paid?: boolean;
  client_id?: string | null;
  client_name?: string | null;
  strategy_id?: string | null;
  strategy_code?: string | null;
  talent_name?: string | null;
  channels?: string[];
  task_id?: string | null;
  task_status?: string | null;
  task_priority?: string | null;
  assignee_id?: string | null;
  assignee_name?: string | null;
  required_designation?: string | null;
}

export async function fetchExecutionCalendar(start: string, end: string, params: Record<string, string | undefined> = {}): Promise<CalendarEvent[]> {
  const clean: Record<string, string> = { start, end };
  for (const [k, v] of Object.entries(params)) if (v) clean[k] = v;
  return (await api.get<CalendarEvent[]>('/calendar/execution', { params: clean })).data;
}
export async function fetchShootsCalendar(start: string, end: string, params: Record<string, string | undefined> = {}): Promise<CalendarEvent[]> {
  const clean: Record<string, string> = { start, end };
  for (const [k, v] of Object.entries(params)) if (v) clean[k] = v;
  return (await api.get<CalendarEvent[]>('/calendar/shoots', { params: clean })).data;
}
export async function fetchPostingCalendar(start: string, end: string, params: Record<string, string | undefined> = {}): Promise<CalendarEvent[]> {
  const clean: Record<string, string> = { start, end };
  for (const [k, v] of Object.entries(params)) if (v) clean[k] = v;
  return (await api.get<CalendarEvent[]>('/calendar/posting', { params: clean })).data;
}

// ---- capacity ----
export interface CapacityStats {
  user_id: string;
  display_name: string;
  role: string;
  designations: string[];
  weekly_capacity: number;
  open_tasks: number;
  overdue_tasks: number;
  completed_last_7d: number;
  utilization_pct: number;
  photo_url?: string | null;
  /** the creatives behind the numbers, from this person's open tasks (2026-09-29) */
  creatives?: { creative_id: string; code: string; title: string; clinic: string; creative_type?: string | null;
                steps: { stage: string; phase?: string | null; status: string; due_date?: string | null; overdue: boolean }[] }[];
}
export async function fetchMyCapacity(): Promise<CapacityStats> {
  return (await api.get<CapacityStats>('/users/me/capacity')).data;
}
export async function fetchTeamCapacity(): Promise<CapacityStats[]> {
  return (await api.get<CapacityStats[]>('/users/team/capacity')).data;
}
export async function fetchUserCapacity(userId: string): Promise<CapacityStats> {
  return (await api.get<CapacityStats>(`/users/${userId}/capacity`)).data;
}
export interface BulkCapacityBody {
  weekly_capacity: number;
  designation?: string;
  user_ids?: string[];
}
export interface BulkCapacityResult { updated_count: number; user_ids: string[] }
export async function bulkCapacity(body: BulkCapacityBody): Promise<BulkCapacityResult> {
  return (await api.post<BulkCapacityResult>('/users/bulk-capacity', body)).data;
}

// ---- analytics dashboard ----
export interface DashboardKpi { label: string; value: number; delta_pct?: number | null }
export interface DashboardBucket { key: string; label: string; count: number }
export interface DashboardTopTalent { talent_id: string; talent_name: string; creative_count: number }
export interface DashboardHotUser { user_id: string; display_name: string; role: string; designations: string[]; weekly_capacity: number; open_tasks: number; overdue_tasks: number; utilization_pct: number }
export interface DashboardUpcomingItem { creative_id: string; code: string; title: string; client_name: string; posting_date: string; creative_type: string; channels: string[] }
export interface DashboardActivity { id: string; actor_name: string; verb: string; target_title?: string | null; at: string }
export interface DashboardOut {
  period_label: string; period_start: string; period_end: string;
  creatives_total: number; creatives_briefed: number; creatives_done: number; creatives_posted: number;
  avg_revision_round: number;
  throughput: DashboardKpi[];
  stage_funnel: DashboardBucket[];
  type_mix: DashboardBucket[];
  funnel_mix: DashboardBucket[];
  channel_mix: DashboardBucket[];
  top_talents: DashboardTopTalent[];
  hot_users: DashboardHotUser[];
  upcoming_postings: DashboardUpcomingItem[];
  recent_activity: DashboardActivity[];
}
export async function fetchDashboard(
  period: string,
  clientId?: string,
  strategyId?: string,
  start?: string,   // YYYY-MM-DD, only used when period === 'custom'
  end?: string,
): Promise<DashboardOut> {
  const params: Record<string, string> = { period };
  if (clientId)   params.client_id = clientId;
  if (strategyId) params.strategy_id = strategyId;
  if (period === 'custom') {
    if (start) params.start = start;
    if (end)   params.end   = end;
  }
  return (await api.get<DashboardOut>('/analytics/dashboard', { params })).data;
}

// ---- media assets (Phase 5) ----
/** Raw folders are free names the team creates per creative. These seven (the
 *  art director's list) are offered as quick picks; anything else is allowed. */
export type RawCategory = string;
export const RAW_SUGGESTIONS = ['Talking', 'Treatments', 'Mapping', 'Before: photos', 'Before: videos', 'After: photos', 'After: videos', 'Reviews', 'Any'];
export const RAW_CATEGORIES: { value: string; label: string }[] = RAW_SUGGESTIONS.map((v) => ({ value: v, label: v }));
export const RAW_CATEGORY_LABEL: Record<string, string> = Object.fromEntries(RAW_SUGGESTIONS.map((v) => [v, v]));
/** Guess a raw folder from the filename: "before_dr_ali.mp4" -> "Before: videos". */
export function guessRawCategory(name: string, isVideo: boolean, folders: string[] = []): string {
  const n = name.toLowerCase();
  const own = folders.find((f) => f.length >= 3 && n.includes(f.toLowerCase()));
  if (own) return own;
  if (/\b(review|testimon|feedback)/.test(n)) return 'Reviews';
  if (/\b(before|pre)\b|_before|before_/.test(n)) return isVideo ? 'Before: videos' : 'Before: photos';
  if (/\b(after|post)\b|_after|after_/.test(n)) return isVideo ? 'After: videos' : 'After: photos';
  if (/talk|interview|speak|vo\b/.test(n)) return 'Talking';
  if (/treat|procedure|session|inject|laser|clean/.test(n)) return 'Treatments';
  if (/map|marking|markup/.test(n)) return 'Mapping';
  return 'Any';
}
export async function addRawFolder(creativeId: string, name: string): Promise<Creative> {
  return (await api.post<Creative>(`/creatives/${creativeId}/raw-folders`, { name })).data;
}
export async function removeRawFolder(creativeId: string, name: string): Promise<Creative> {
  return (await api.delete<Creative>(`/creatives/${creativeId}/raw-folders/${encodeURIComponent(name)}`)).data;
}
export type AssetKind = 'raw' | 'edit' | 'final' | 'static' | 'poster' | 'reference' | 'brief_doc' | 'thumbnail';

export interface MediaAsset {
  id: string;
  creative_id: string;
  kind: AssetKind;
  filename: string;
  storage_path: string;
  content_type?: string | null;
  size_bytes?: number | null;
  width?: number | null;
  height?: number | null;
  duration_sec?: number | null;
  thumbnail_path?: string | null;
  version_label?: string | null;
  category?: RawCategory | string | null;
  is_latest_of_kind: boolean;
  is_favorite?: boolean;            // migration 035: the star, a best shot
  /** false once deleted; a deleted version still shows in the round it was sent in */
  active?: boolean;
  uploaded_by?: string | null;
  /** may the signed-in person delete it (set by the server per request) */
  can_delete?: boolean | null;
  /** the clinic approved this file: locked (migration 079) */
  client_approved?: boolean | null;
  uploaded_at: string;
  finalized_at?: string | null;
  notes?: string | null;
  // signed URLs filled by API
  download_url?: string | null;
  attachment_url?: string | null;   // same file, Content-Disposition: attachment
  download_name?: string | null;    // "MS-3-2026-011 - Title - raw - Reviews - C0198.MP4"
  thumbnail_url?: string | null;
  // Migration 033: 1080p proxy the player streams; download stays the master
  proxy_status?: 'none' | 'queued' | 'pending' | 'ready' | 'failed' | string;
  proxy_bytes?: number | null;
  proxy_url?: string | null;
  /** false = the master cannot play in a browser; only the web copy shows it */
  web_playable?: boolean | null;
  proxy_eta_seconds?: number | null;   // only while queued/pending
  proxy_progress?: number | null;
  // Migration 039: raw material facets, the link to the library row, and usage
  client_id?: string | null;
  material_doctor?: string | null;
  material_procedure?: string | null;
  material_date?: string | null;
  material_tags?: string[];
  source_material_id?: string | null;
  used_count?: number | null;
  used_in?: { creative_id: string; code: string; title: string; stage: string }[] | null;
  // Phase 5.3: scrubber-hover thumbnail sprite
  preview_sprite_url?: string | null;
  preview_sprite_cols?: number | null;
  preview_sprite_rows?: number | null;
  preview_sprite_count?: number | null;
  preview_tile_w?: number | null;
  preview_tile_h?: number | null;
}

export interface UploadUrlResponse {
  asset_id: string;
  upload_url: string;
  storage_path: string;
  thumbnail_upload_url: string;
  thumbnail_path: string;
}

/** Every file on a creative. The list comes in pages of 500 (a shoot can leave
 *  well over a thousand); the first answer says how many there are and the rest
 *  are fetched side by side, three at a time. */
/** Kinds whose category is a folder path (api/app/routers/assets.py FOLDER_KINDS). */
export const FOLDER_KINDS: AssetKind[] = ['raw', 'reference', 'static', 'poster', 'brief_doc'];

/** Rename or move a folder of one kind; its files and subfolders follow. */
export async function renameFolder(creativeId: string, kind: AssetKind, oldPath: string, newPath: string): Promise<{ moved: number; folder: string }> {
  return (await api.post<{ moved: number; folder: string }>(`/creatives/${creativeId}/folders/rename`, { kind, old: oldPath, new: newPath })).data;
}

/** A Word document as cleaned HTML for the in-app reader (api/app/doc_reader.py). */
export interface ReadableDocument { status: 'ok' | 'unsupported' | 'too_big' | 'failed'; html?: string | null; message?: string | null }
export async function fetchDocument(assetId: string): Promise<ReadableDocument> {
  return (await api.get<ReadableDocument>(`/assets/${assetId}/document`, { timeout: 90000 })).data;
}

export async function fetchAssets(creativeId: string, kind?: string, includeArchived = false): Promise<MediaAsset[]> {
  const PAGE = 500;
  const get = (offset: number) => api.get<MediaAsset[]>(`/creatives/${creativeId}/assets`, { params: { ...(kind ? { kind } : {}), ...(includeArchived ? { include_archived: true } : {}), offset, limit: PAGE } });
  const first = await get(0);
  const total = Number(first.headers['x-total-count'] ?? first.data.length);
  const out = [...first.data];
  const offsets: number[] = [];
  for (let o = PAGE; o < total; o += PAGE) offsets.push(o);
  for (let i = 0; i < offsets.length; i += 3) {
    const pages = await Promise.all(offsets.slice(i, i + 3).map(get));
    for (const p of pages) out.push(...p.data);
  }
  // a file uploaded between pages can shift one along; show each once
  const seen = new Set<string>();
  return out.filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)));
}
export interface ResumableUrl { asset_id: string; session_url: string; storage_path: string; thumbnail_upload_url: string; thumbnail_path: string }
export async function getResumableUrl(creativeId: string, body: { kind: AssetKind; category?: string; filename: string; content_type: string; size_bytes: number }): Promise<ResumableUrl> {
  return (await api.post<ResumableUrl>(`/creatives/${creativeId}/assets/resumable-url`, body)).data;
}
export async function getUploadUrl(creativeId: string, body: { kind: AssetKind; category?: string; filename: string; content_type: string; size_bytes: number }): Promise<UploadUrlResponse> {
  return (await api.post<UploadUrlResponse>(`/creatives/${creativeId}/assets/upload-url`, body)).data;
}
export async function finalizeAsset(creativeId: string, assetId: string, body: { width?: number; height?: number; duration_sec?: number; version_label?: string; notes?: string; has_thumbnail: boolean }): Promise<MediaAsset> {
  return (await api.post<MediaAsset>(`/creatives/${creativeId}/assets/${assetId}/finalize`, body, { timeout: 60_000 })).data;
}
export interface MaterialClient { id: string; name: string; logo_url?: string | null; count: number; doctors?: string[]; procedures?: string[]; procedure_label?: string }
export async function fetchMaterialClients(): Promise<MaterialClient[]> {
  return (await api.get<MaterialClient[]>('/material/clients')).data;
}
export async function fetchMaterial(clientId: string, f: { doctor?: string; procedure?: string; on?: string; q?: string; used?: string }): Promise<MediaAsset[]> {
  const params: Record<string, string> = { client_id: clientId };
  for (const [k, v] of Object.entries(f)) if (v) params[k] = v;
  return (await api.get<MediaAsset[]>('/material', { params })).data;
}
export interface MaterialFacets { doctor?: string; procedure?: string; date?: string; tags?: string[] }
export async function getMaterialResumableUrl(clientId: string, body: { filename: string; content_type: string; size_bytes: number } & MaterialFacets): Promise<ResumableUrl> {
  return (await api.post<ResumableUrl>('/material/resumable-url', { client_id: clientId, ...body })).data;
}
export async function getMaterialUploadUrl(clientId: string, body: { filename: string; content_type: string; size_bytes: number } & MaterialFacets): Promise<UploadUrlResponse> {
  return (await api.post<UploadUrlResponse>('/material/upload-url', { client_id: clientId, ...body })).data;
}
export async function finalizeMaterial(assetId: string, body: { width?: number; height?: number; duration_sec?: number; notes?: string; has_thumbnail: boolean }): Promise<MediaAsset> {
  return (await api.post<MediaAsset>(`/material/${assetId}/finalize`, body, { timeout: 60_000 })).data;
}
export async function patchMaterial(assetId: string, body: MaterialFacets): Promise<MediaAsset> {
  return (await api.patch<MediaAsset>(`/material/${assetId}`, body)).data;
}
export async function deleteMaterial(assetId: string): Promise<void> {
  await api.delete(`/material/${assetId}`);
}
/** Many files at once: move (re-file), copy (a second entry with the same bytes), delete, or add tags. Only the facets given change. */
export async function bulkMaterial(assetIds: string[], action: 'move' | 'copy' | 'delete' | 'tag', facets: MaterialFacets = {}): Promise<{ affected: number; created_ids: string[] }> {
  const out = { affected: 0, created_ids: [] as string[] };
  for (let i = 0; i < assetIds.length; i += 200) {
    const r = (await api.post<{ affected: number; created_ids: string[] }>('/material/bulk', { asset_ids: assetIds.slice(i, i + 200), action, ...facets })).data;
    out.affected += r.affected; out.created_ids.push(...(r.created_ids ?? []));
  }
  return out;
}
export async function linkMaterial(creativeId: string, materialIds: string[]): Promise<MediaAsset[]> {
  return (await api.post<MediaAsset[]>(`/creatives/${creativeId}/material-links`, { material_ids: materialIds })).data;
}
export async function patchAsset(assetId: string, body: { version_label?: string; notes?: string; is_latest_of_kind?: boolean; is_favorite?: boolean; category?: RawCategory }): Promise<MediaAsset> {
  return (await api.patch<MediaAsset>(`/assets/${assetId}`, body)).data;
}
export async function deleteAsset(assetId: string): Promise<void> {
  await api.delete(`/assets/${assetId}`);
}
/** One zip of the chosen assets (empty = all the caller may see). Saves through the browser. */
export interface ZipJob {
  id: string; status: 'building' | 'ready' | 'failed' | string; filename: string;
  file_count: number; total_bytes: number; done_bytes: number; pct: number;
  error?: string | null; url?: string | null;
}
/** Start a zip. The archive is built in the background into storage; poll
 *  zipJobStatus until ready, then send the browser to `url`. */
export async function startAssetsZip(creativeId: string, assetIds: string[]): Promise<ZipJob> {
  return (await api.post<ZipJob>(`/creatives/${creativeId}/assets/zip`, { asset_ids: assetIds })).data;
}
export async function zipJobStatus(jobId: string): Promise<ZipJob> {
  return (await api.get<ZipJob>(`/zip-jobs/${jobId}`)).data;
}
/** Start, wait with progress, then let the browser download the ready bundle. */
export async function downloadAssetsZip(creativeId: string, assetIds: string[], onProgress?: (j: ZipJob) => void): Promise<ZipJob> {
  let job = await startAssetsZip(creativeId, assetIds);
  onProgress?.(job);
  const started = Date.now();
  while (job.status === 'building') {
    if (Date.now() - started > 2 * 3600_000) throw new Error('The zip took too long');
    await new Promise((r) => setTimeout(r, 1000));
    job = await zipJobStatus(job.id);
    onProgress?.(job);
  }
  if (job.status !== 'ready' || !job.url) throw new Error(job.error || 'Zip failed');
  const a = document.createElement('a');
  a.href = job.url; a.rel = 'noreferrer'; document.body.appendChild(a); a.click(); a.remove();
  return job;
}
/** Mahmoud 2026-09-28: ask for a web copy of a master this browser cannot play. */
export async function requestWebCopy(assetId: string): Promise<MediaAsset> {
  const { data } = await api.post<MediaAsset>(`/assets/${assetId}/web-copy`);
  return data;
}

export async function bulkAssets(assetIds: string[], action: 'delete' | 'set_kind' | 'set_category' | 'set_folder', kind?: AssetKind, category?: string): Promise<{ affected: number; skipped: string[] }> {
  // "Everything" on a big creative can be hundreds of files; the server takes 200 a call
  const out = { affected: 0, skipped: [] as string[] };
  for (let i = 0; i < assetIds.length; i += 200) {
    const r = (await api.post<{ affected: number; skipped: string[] }>('/assets/bulk', { asset_ids: assetIds.slice(i, i + 200), action, kind, category })).data;
    out.affected += r.affected; out.skipped.push(...(r.skipped ?? []));
  }
  return out;
}
/** Record -> storage. Returns the storage path to attach to a comment or review. */
export async function uploadVoice(blob: Blob): Promise<string> {
  const r = await api.post<{ path: string; upload_url: string }>('/voice/upload-url', { content_type: blob.type || 'audio/webm', size_bytes: blob.size });
  const put = await fetch(r.data.upload_url, { method: 'PUT', body: blob, headers: { 'Content-Type': blob.type || 'audio/webm' } });
  if (!put.ok) throw new Error(`voice upload failed (${put.status})`);
  return r.data.path;
}
export interface StageSla { brief: number; shoot: number; raws_review: number; edit: number; dual_review: number; client_review: number; revision_fraction: number }
export async function fetchStageSla(): Promise<StageSla> {
  return (await api.get<StageSla>('/admin/settings/stage-sla')).data;
}
export async function saveStageSla(body: StageSla): Promise<StageSla> {
  return (await api.put<StageSla>('/admin/settings/stage-sla', body)).data;
}
export async function handbackTask(id: string, reason: string, mentions: string[] = []): Promise<Task> {
  return (await api.post<Task>(`/tasks/${id}/handback`, { reason, mentions })).data;
}
export async function regenerateAssetPreview(assetId: string): Promise<void> {
  await api.post(`/assets/${assetId}/regenerate-preview`);
}


// ---------- users (for @mention typeahead etc) ----------
export interface TeamMember {
  id: string;
  email: string;
  display_name: string;
  role: 'owner' | 'manager' | 'agent' | 'crew' | 'freelancer' | 'client';
  designations: string[];
  active: boolean;
  photo_url?: string | null;
}

export async function fetchUsers(): Promise<TeamMember[]> {
  return (await api.get<TeamMember[]>('/users')).data;
}


// ---------- Telegram link ----------
export interface TelegramStatus {
  linked: boolean;
  linked_at?: string | null;
  has_pending_code: boolean;
  telegram_pref_on: boolean;
}
export async function fetchTelegramStatus(): Promise<TelegramStatus> {
  return (await api.get<TelegramStatus>('/telegram/status')).data;
}
export async function fetchTelegramLinkCode(): Promise<{ code: string; instructions: string }> {
  return (await api.get<{ code: string; instructions: string }>('/telegram/link-code')).data;
}
export async function rotateTelegramLinkCode(): Promise<{ code: string }> {
  return (await api.post<{ code: string }>('/telegram/link-code')).data;
}
export async function unlinkTelegram(): Promise<void> {
  await api.delete('/telegram/link');
}


// ---------- Password set/change ----------
export async function setPassword(body: { current_password?: string; new_password: string }): Promise<void> {
  await api.post('/auth/password/set', body);
}


// ---------- Phase 5.2: Asset Reviews (Frame.io-style) ----------
export type ReviewStatus = 'open' | 'resolved';

export type DrawingShape =
  | { type: 'rect';     x: number; y: number; w: number; h: number; color: string; stroke?: number }
  | { type: 'path';     d: string; color: string; stroke?: number }
  | { type: 'arrow';    x1: number; y1: number; x2: number; y2: number; color: string; stroke?: number }
  | { type: 'text';     x: number; y: number; body: string; color: string; size?: number }
  | { type: 'ellipse';  x: number; y: number; w: number; h: number; color: string; stroke?: number };

export interface AssetReview {
  id: string;
  asset_id: string;
  creative_id: string;
  audio_url?: string | null;
  parent_id?: string | null;
  timestamp_sec?: number | null;
  drawing?: DrawingShape[] | null;
  body: string;
  status: ReviewStatus;
  resolved_by?: string | null;
  resolved_at?: string | null;
  mentioned_user_ids: string[];
  author_id: string;
  author_name: string;
  created_at: string;
  edited_at?: string | null;
}

export async function fetchAssetReviews(assetId: string, includeResolved = true): Promise<AssetReview[]> {
  return (await api.get<AssetReview[]>(`/assets/${assetId}/reviews`, { params: { include_resolved: includeResolved } })).data;
}
export async function fetchCreativeReviews(creativeId: string): Promise<AssetReview[]> {
  return (await api.get<AssetReview[]>(`/creatives/${creativeId}/reviews`)).data;
}
export async function createReview(assetId: string, body: {
  body: string;
  audio_path?: string | null;
  timestamp_sec?: number | null;
  drawing?: DrawingShape[] | null;
  parent_id?: string | null;
  mentioned_user_ids?: string[];
}): Promise<AssetReview> {
  return (await api.post<AssetReview>(`/assets/${assetId}/reviews`, body)).data;
}
export async function patchReview(reviewId: string, body: {
  body?: string;
  drawing?: DrawingShape[] | null;
  status?: ReviewStatus;
}): Promise<AssetReview> {
  return (await api.patch<AssetReview>(`/reviews/${reviewId}`, body)).data;
}
export async function deleteReview(reviewId: string): Promise<void> {
  await api.delete(`/reviews/${reviewId}`);
}


// ---------- Phase 5.2: Notifications ----------
export interface Notification {
  creative_code?: string | null; creative_title?: string | null; for_you?: boolean;
  id: string;
  kind: string;
  title: string;
  body?: string | null;
  link?: string | null;
  related_creative_id?: string | null;
  related_asset_id?: string | null;
  related_review_id?: string | null;
  actor_id?: string | null;
  actor_name?: string | null;
  read: boolean;
  read_at?: string | null;
  created_at: string;
}

export async function fetchNotifications(opts: { unread_only?: boolean; mine_only?: boolean } = {}): Promise<Notification[]> {
  return (await api.get<Notification[]>('/notifications', { params: opts })).data;
}
export async function fetchUnreadCount(): Promise<number> {
  return (await api.get<{ count: number }>('/notifications/unread-count')).data.count;
}
export async function markNotificationRead(id: string): Promise<Notification> {
  return (await api.post<Notification>(`/notifications/${id}/read`)).data;
}
export async function markAllNotificationsRead(): Promise<void> {
  await api.post('/notifications/read-all');
}


// ---------- Phase 5.4: Offline-backup-drive heartbeat ----------
export interface BackupStatus {
  last_run_at?: string | null;
  status?: 'ok' | 'partial' | 'fail' | null;
  media_files?: number | null;
  media_mb?: number | null;
  db_dumps?: number | null;
  host?: string | null;
  os?: string | null;
  health: 'healthy' | 'stale' | 'offline';
  hours_since?: number | null;
  // Live presence: whether the drive is plugged in RIGHT NOW, which is separate
  // from when a backup last completed.
  device_online?: boolean;
  device_host?: string | null;
  device_os?: string | null;
  drive_present?: boolean;
  drive_label?: string | null;
  free_gb?: number | null;
  device_last_seen_at?: string | null;
  minutes_since_seen?: number | null;
  pending_request?: boolean;
  pending_requested_by?: string | null;
  pending_requested_at?: string | null;
  // Live progress of a run in flight. Phase based, not a smooth percentage.
  run_active?: boolean;
  run_phase?: string | null;
  run_percent?: number | null;
  run_detail?: string | null;
  run_started_at?: string | null;
  run_finished_at?: string | null;
  run_status?: 'running' | 'ok' | 'fail' | 'partial' | null;
}
export async function fetchBackupStatus(): Promise<BackupStatus> {
  return (await api.get<BackupStatus>('/backup/status')).data;
}

export interface BackupRequest {
  id: string;
  requested_at: string;
  requested_by_name?: string | null;
  status: 'pending' | 'claimed' | 'done' | 'failed';
}
/** Ask the connected machine to back up. It picks the job up on its next
 *  check-in, so this returns immediately rather than waiting on a laptop. */
export async function requestBackup(): Promise<BackupRequest> {
  return (await api.post<BackupRequest>('/backup/requests')).data;
}

// ---------- Admin panel ----------
export interface ScopeItem { key: string; description: string; group?: string; label?: string; hint?: string; }
export interface ScopeCatalogue {
  scopes: ScopeItem[];
  role_defaults: Record<string, string[]>;
  designation_defaults?: Record<string, string[]>;
  my_scopes: string[];
}
export interface AdminUser {
  id: string; email: string; display_name: string; role: string;
  designations: string[]; client_id?: string | null; active: boolean;
  last_seen_at?: string | null; created_at: string; weekly_capacity: number;
  telegram_linked: boolean;
  extra_scopes: string[]; denied_scopes: string[];
  effective_scopes: string[]; role_scopes: string[];
  // per-client visibility; ignored for owner and manager
  client_ids: string[]; access_level: 'clients' | 'tasks'; unrestricted: boolean;
  default_crew: boolean;
  can_upload_material?: boolean;
  portal_scope?: 'full' | 'doctor' | 'uploads';
  portal_talent_id?: string | null;
  portal_talent_ids?: string[];
}
export interface AdminOverview {
  users_total: number; users_active: number; users_by_role: Record<string, number>;
  clients_total: number; creatives_total: number;
  tasks_open: number; tasks_blocked: number; never_signed_in: number; no_signin_recorded: number;
}
export interface AdminTask {
  id: string; creative_id: string; creative_code: string; creative_title: string;
  client_id: string; stage: string; required_designation?: string | null;
  assignee_id?: string | null; assignee_name?: string | null;
  status: string; priority: string; due_date?: string | null; created_at: string;
}
export interface AuditRow {
  id: string; at: string; actor_name?: string | null; verb: string;
  target_type?: string | null; target_title?: string | null;
  meta?: Record<string, unknown> | null;
}
/** What the CALLER may do. Drives which controls render, so the UI never shows
 *  a button that would 403. */
export interface MyScopes {
  role: string; scopes: string[]; is_admin: boolean;
  unrestricted: boolean; client_ids: string[]; access_level: 'clients' | 'tasks';
}

export async function fetchMyScopes(): Promise<MyScopes> {
  return (await api.get<MyScopes>('/admin/me/scopes')).data;
}
export async function fetchAdminOverview(): Promise<AdminOverview> {
  return (await api.get<AdminOverview>('/admin/overview')).data;
}
export async function fetchScopeCatalogue(): Promise<ScopeCatalogue> {
  return (await api.get<ScopeCatalogue>('/admin/scopes')).data;
}
export async function fetchAdminUsers(params?: { q?: string; role?: string; active?: boolean }): Promise<AdminUser[]> {
  return (await api.get<AdminUser[]>('/admin/users', { params })).data;
}
export async function setUserRole(id: string, role: string): Promise<AdminUser> {
  return (await api.patch<AdminUser>(`/admin/users/${id}/role`, { role })).data;
}
export async function setContactUploadsMaterial(id: string, on: boolean): Promise<void> {
  await api.patch(`/users/${id}`, { can_upload_material: on });
}
/** A portal login's reach: the whole clinic, one doctor, or uploads only. */
export async function setContactPortalScope(id: string, scope: 'full' | 'doctor' | 'uploads', talentId?: string | null, talentIds?: string[]): Promise<void> {
  await api.patch(`/users/${id}`, { portal_scope: scope, portal_talent_id: scope === 'doctor' ? (talentId ?? '') : '', portal_talent_ids: scope === 'doctor' ? (talentIds ?? []) : [] });
}
/** The doctor's own portal login, tied to their talent entry. The magic link is emailed and returned. */
/** Take a clinic off a doctor's login; her other clinics stay. */
export async function leavePortalClinic(clientId: string, userId: string): Promise<{ id: string; client_id?: string | null }> {
  return (await api.delete(`/clients/${clientId}/portal-users/${userId}`)).data;
}
export async function invitePortalDoctor(clientId: string, talentId: string, email: string): Promise<{ user: { id: string; email: string; display_name: string }; invite_url: string; emailed: boolean }> {
  return (await api.post(`/clients/${clientId}/talents/${talentId}/portal-invite`, { email })).data;
}
export async function setUserActive(id: string, active: boolean): Promise<AdminUser> {
  return (await api.patch<AdminUser>(`/admin/users/${id}/active`, { active })).data;
}
export interface DeletedUser { deleted: boolean; display_name: string; email: string }
/** Remove an account for good. The API refuses (409) when history points at them,
 *  and the message says exactly what holds them. */
export async function deleteUser(id: string): Promise<DeletedUser> {
  return (await api.delete<DeletedUser>(`/admin/users/${id}`)).data;
}
/** Which clinics a person sees, and whether the whole clinic or only their own tasks. */
export async function setUserAccess(id: string, client_ids: string[], access_level: 'clients' | 'tasks', default_crew?: boolean): Promise<AdminUser> {
  return (await api.patch<AdminUser>(`/admin/users/${id}/access`, { client_ids, access_level, default_crew })).data;
}
/** See the app as this person, read-only, for 30 minutes. Owner/manager only; audited. */
export async function impersonateUser(id: string, clientId?: string): Promise<{ access_token: string; expires_in: number; user: { id: string; display_name: string; role: string } }> {
  return (await api.post(`/admin/users/${id}/impersonate`, clientId ? { client_id: clientId } : undefined)).data;
}
export interface ClientTeamMember { id: string; display_name: string; role: string; designations: string[]; access_level: 'clients' | 'tasks'; default_crew: boolean }
export async function fetchClientTeam(clientId: string): Promise<ClientTeamMember[]> {
  return (await api.get<ClientTeamMember[]>(`/clients/${clientId}/team`)).data;
}
export async function addTeamMember(clientId: string, userId: string): Promise<ClientTeamMember[]> {
  return (await api.post<ClientTeamMember[]>(`/clients/${clientId}/team`, { user_id: userId })).data;
}
export async function removeTeamMember(clientId: string, userId: string): Promise<ClientTeamMember[]> {
  return (await api.delete<ClientTeamMember[]>(`/clients/${clientId}/team/${userId}`)).data;
}
export async function setUserScopes(id: string, extra_scopes: string[], denied_scopes: string[]): Promise<AdminUser> {
  return (await api.patch<AdminUser>(`/admin/users/${id}/scopes`, { extra_scopes, denied_scopes })).data;
}
export async function fetchAdminTasks(params?: Record<string, unknown>): Promise<AdminTask[]> {
  return (await api.get<AdminTask[]>('/admin/tasks', { params })).data;
}
export async function reassignTask(id: string, assignee_id: string | null): Promise<AdminTask> {
  return (await api.patch<AdminTask>(`/admin/tasks/${id}/reassign`, { assignee_id })).data;
}
export async function fetchAudit(limit = 100): Promise<AuditRow[]> {
  return (await api.get<AuditRow[]>('/admin/audit', { params: { limit } })).data;
}


// ---- client sign-off (portal) -------------------------------------------------
export interface PortalApproval {
  status: 'pending' | 'approved' | 'adjusting';
  revision_round: number;
  approved_at?: string | null;
  approved_by_name?: string | null;
  adjustments_used: number;
  adjustments_limit: number;
  adjustments_left: number;
  can_approve: boolean;
  can_request: boolean;
  markers_this_round: number;
  you_are_approver?: boolean;
  awaiting_names?: string[];
  /** every final file the clinic decides on: approved (locked) or not, and its open notes */
  files?: PortalFileState[];
}
export interface PortalFileState { asset_id: string; approved: boolean; approved_at?: string | null; approved_by_name?: string | null; notes: number }
export async function fetchPortalApproval(creativeId: string): Promise<PortalApproval> {
  return (await api.get<PortalApproval>(`/portal/creatives/${creativeId}/approval`)).data;
}
/** Final. The server refuses a second approval and any adjustment afterwards. */
export async function portalApprove(creativeId: string, pick: { assetIds?: string[]; all?: boolean } = { all: true }): Promise<PortalApproval> {
  return (await api.post<PortalApproval>(`/portal/creatives/${creativeId}/approve`,
    { confirm: true, asset_ids: pick.assetIds ?? null, all: !!pick.all })).data;
}
export async function portalRequestAdjustment(creativeId: string, notes: string): Promise<PortalApproval> {
  return (await api.post<PortalApproval>(`/portal/creatives/${creativeId}/request-adjustment`, { notes })).data;
}
/** Owner/manager: more adjustment rounds on one creative, with a reason. */
export async function grantClientAdjustments(creativeId: string, extra: number, reason: string, mentions: string[] = []): Promise<Creative> {
  return (await api.post<Creative>(`/creatives/${creativeId}/client-adjustments/grant`, { extra, reason, mentions })).data;
}
export async function setClientSettings(clientId: string, client_adjustment_limit: number): Promise<Client> {
  return (await api.patch<Client>(`/clients/${clientId}/settings`, { client_adjustment_limit })).data;
}

// ---------- Shoots + script approval by the doctor ----------
export interface Shoot {
  creative_id: string; code: string; title: string; creative_type: string;
  client_id: string; client_name: string; strategy_id: string; strategy_code: string; current_stage: string;
  shoot_id?: string | null;
  shoot_at?: string | null; shoot_location?: string | null; shoot_notes?: string | null;
  talent_id?: string | null; talent_name?: string | null; talent_telegram_linked: boolean;
  script_status: 'draft' | 'sent' | 'approved' | 'changes_requested';
  /** false for a coordinator piece or a general clinic video: nobody approves the script */
  script_needs_doctor: boolean;
  script_sent_at?: string | null; script_decided_at?: string | null; script_feedback?: string | null;
  has_script: boolean; videographer_id?: string | null; videographer_name?: string | null;
}
export interface ShootDetail extends Shoot {
  script?: string | null;
  script_html?: string | null;
  talents: Array<{ id: string; name: string; telegram_linked: boolean }>;
  bot_username: string;
  shoot?: ShootSessionBrief | null;
  eligible_shoots: ShootSessionBrief[];
  can_join: boolean;
  join_blocker?: string | null;
}
export interface ShootSessionBrief { id: string; title: string; shoot_at: string; location?: string | null; client_id: string; client_name: string; status: string }
export interface ShootCreative {
  id: string; code: string; title: string; creative_type: string; current_stage: string;
  talent_id?: string | null; talent_name?: string | null; talent_telegram_linked: boolean;
  script_status: 'draft' | 'sent' | 'approved' | 'changes_requested'; script_needs_doctor: boolean; has_script: boolean;
  videographer_id?: string | null; videographer_name?: string | null; eligible: boolean; reason?: string | null; raw_count?: number;
  /** the shoot day, from the crew's side */
  script?: string | null;
  shot_at?: string | null;
  shot_by_name?: string | null;
  script_changed_at?: string | null;
  script_changed_by_name?: string | null;
}
export interface ShootSession {
  id: string; client_id: string; client_name: string; title: string; shoot_at: string; location?: string | null; notes?: string | null;
  status: 'proposed' | 'scheduled' | 'declined' | 'done' | 'cancelled' | 'planned'; created_by?: string | null; created_at: string; creative_count: number; ready_count: number;
  duration_minutes: number; proposed_by?: string | null; proposed_by_name?: string | null; approved_by_name?: string | null; approved_at?: string | null;
  decline_note?: string | null; approver_names: string[]; can_decide: boolean; can_plan: boolean; approver_phones: { name: string; phone: string }[];
  creatives: ShootCreative[]; doctors: string[];
  crew_user_ids: string[]; crew: Array<{ id: string; name: string; designations: string[] }>;
  /** the calendar invites (api shoot_invites.py) */
  invites?: { team?: number; clinic?: number; clinic_fallback?: boolean; error?: string | null; synced_at?: string; cancelled?: boolean; dry_run?: boolean };
}
export interface ShootBusy { shoot_id: string; title: string; shoot_at: string; ends_at: string }
export interface ShootCrewCandidate { id: string; display_name: string; designations: string[]; on_client: boolean; busy: ShootBusy[] }
export async function fetchShootCrewCandidates(clientId: string, at?: string, durationMinutes?: number, exceptId?: string): Promise<ShootCrewCandidate[]> {
  return (await api.get<ShootCrewCandidate[]>('/shoots/candidates', { params: { client_id: clientId, at, duration_minutes: durationMinutes, except_id: exceptId } })).data;
}
export async function fetchJoinableCreatives(clientId: string): Promise<ShootCreative[]> {
  return (await api.get<ShootCreative[]>('/shoots/joinable', { params: { client_id: clientId } })).data;
}
export async function decideShoot(id: string, decision: 'approved' | 'declined', note?: string): Promise<ShootSessionDetail> {
  return (await api.post<ShootSessionDetail>(`/shoots/${id}/decide`, { decision, note })).data;
}
export interface ShootSessionDetail extends ShootSession { eligible: ShootCreative[]; not_ready: ShootCreative[] }
export interface TalentCode { talent_id: string; talent_name: string; code: string; bot_username: string; bot_url: string; command: string }
export async function fetchShoots(from?: string, to?: string, client_id?: string): Promise<ShootSession[]> {
  return (await api.get<ShootSession[]>('/shoots', { params: { from, to, client_id } })).data;
}
export async function fetchShootSession(id: string): Promise<ShootSessionDetail> {
  return (await api.get<ShootSessionDetail>(`/shoots/${id}`)).data;
}
export async function createShootSession(body: { client_id: string; title?: string; shoot_at: string; duration_minutes?: number; location?: string; notes?: string; crew_user_ids?: string[]; creative_ids?: string[] }): Promise<ShootSessionDetail> {
  return (await api.post<ShootSessionDetail>('/shoots', body)).data;
}
export async function patchShootSession(id: string, body: { title?: string; shoot_at?: string; duration_minutes?: number; location?: string; notes?: string; status?: string; crew_user_ids?: string[] }): Promise<ShootSessionDetail> {
  return (await api.patch<ShootSessionDetail>(`/shoots/${id}`, body)).data;
}
export async function deleteShootSession(id: string): Promise<void> {
  await api.delete(`/shoots/${id}`);
}
export async function addCreativesToShoot(id: string, creative_ids: string[]): Promise<ShootSessionDetail> {
  return (await api.post<ShootSessionDetail>(`/shoots/${id}/creatives`, { creative_ids })).data;
}
/** The crew marking a piece filmed on the day, before any file lands. */
export async function markShot(creativeId: string, shot: boolean): Promise<unknown> {
  return (await api.post(`/creatives/${creativeId}/shot`, { shot })).data;
}
/** What the doctor actually said, when it was not the approved script. */
export async function rewriteScriptOnShoot(creativeId: string, script: string, note?: string): Promise<unknown> {
  return (await api.post(`/creatives/${creativeId}/script/rewrite`, { script, note })).data;
}
export async function removeCreativeFromShoot(id: string, creativeId: string): Promise<ShootSessionDetail> {
  return (await api.delete<ShootSessionDetail>(`/shoots/${id}/creatives/${creativeId}`)).data;
}
export async function fetchShoot(creativeId: string): Promise<ShootDetail> {
  return (await api.get<ShootDetail>(`/creatives/${creativeId}/shoot`)).data;
}
export async function patchShoot(creativeId: string, body: {
  shoot_notes?: string; talent_id?: string; script?: string; script_html?: string; script_needs_doctor?: boolean; reason?: string;
}): Promise<ShootDetail> {
  return (await api.patch<ShootDetail>(`/creatives/${creativeId}/shoot`, body)).data;
}
export async function sendScript(creativeId: string): Promise<ShootDetail> {
  return (await api.post<ShootDetail>(`/creatives/${creativeId}/script/send`)).data;
}
export async function recordScriptDecision(creativeId: string, body: { decision: 'approved' | 'changes_requested'; feedback?: string; mentions?: string[] }): Promise<ShootDetail> {
  return (await api.post<ShootDetail>(`/creatives/${creativeId}/script/decision`, body)).data;
}
export async function talentTelegramCode(clientId: string, talentId: string): Promise<TalentCode> {
  return (await api.post<TalentCode>(`/clients/${clientId}/talents/${talentId}/telegram-code`)).data;
}
export async function unlinkTalentTelegram(clientId: string, talentId: string): Promise<void> {
  await api.delete(`/clients/${clientId}/talents/${talentId}/telegram`);
}

// ---------- Production tracker (ADR-019) ----------
export interface TrackStep { key: string; label: string; state: 'done' | 'current' | 'blocked' | 'pending' | 'skipped'; at?: string | null; by?: string | null; note?: string | null; due_at?: string | null; overdue?: boolean }
export interface TrackNext { key: string; label: string; owner?: string | null; owner_id?: string | null; since?: string | null; days: number; blocked: boolean; due_at?: string | null; overdue?: boolean }
export interface TrackRow {
  creative_id: string; code: string; title: string; creative_type: string; client_id: string; client_name: string;
  current_stage: string; status: string; talent_name?: string | null; script_status: string; shoot_id?: string | null; shoot_at?: string | null;
  steps: TrackStep[]; next?: TrackNext | null;
}
export interface TrackSummary {
  total_open: number; blocked: number; overdue: number; by_next: Record<string, number>; shoots_next_7: number; shoots_no_crew: number;
  on_shoot_not_ready: number; waiting_doctor_oldest_days: number; labels: Record<string, string>;
}
export async function fetchTrack(client_id?: string, include_done = false): Promise<TrackRow[]> {
  return (await api.get<TrackRow[]>('/track', { params: { client_id, include_done } })).data;
}
export async function fetchTrackSummary(client_id?: string): Promise<TrackSummary> {
  return (await api.get<TrackSummary>('/track/summary', { params: { client_id } })).data;
}
// ---- the portal's tracker and scripts (the doctor's side)
export interface PortalStep { key: string; label: string; state: 'done' | 'current' | 'blocked' | 'pending'; at?: string | null }
export interface PortalTrackRow {
  creative_id: string; code: string; title: string; creative_type: string; talent_id?: string | null; talent_name?: string | null;
  current_stage: string; status: string; client_visible: boolean; script_status: string; steps: PortalStep[]; next_key: string; next_label: string;
  owner?: string | null; waiting_on_you: boolean; waiting_on_doctor: boolean; since?: string | null; days: number; expected_by?: string | null;
  overdue: boolean; blocked: boolean; shoot_at?: string | null; posting_date?: string | null;
}
export interface PortalTrack {
  me_talent_id?: string | null; reach?: string[] | null; doctors: Array<{ id: string; name: string; me: boolean; open: number }>;
  summary: { open: number; waiting_on_you: number; waiting_on_doctor: number; overdue: number; shoots_next_7: number; posted: number; by_next: Record<string, number>; labels: Record<string, string> };
  rows: PortalTrackRow[];
}
export async function fetchPortalTrack(includeDone = false): Promise<PortalTrack> {
  return (await api.get<PortalTrack>('/portal/track', { params: { include_done: includeDone } })).data;
}
export interface PortalScript {
  creative_id: string; code: string; title: string; creative_type: string; talent_id?: string | null; talent_name?: string | null; script: string; script_html?: string | null;
  script_status: 'sent' | 'approved' | 'changes_requested' | 'draft'; sent_at?: string | null; decided_at?: string | null; feedback?: string | null;
  shoot_at?: string | null; shoot_location?: string | null; client_visible: boolean; mine: boolean; can_decide: boolean;
}
export async function fetchPortalScripts(): Promise<PortalScript[]> {
  return (await api.get<PortalScript[]>('/portal/scripts')).data;
}
export async function portalScriptApprove(creativeId: string): Promise<PortalScript> {
  return (await api.post<PortalScript>(`/portal/creatives/${creativeId}/script/approve`)).data;
}
export async function portalScriptChanges(creativeId: string, feedback: string): Promise<PortalScript> {
  return (await api.post<PortalScript>(`/portal/creatives/${creativeId}/script/changes`, { feedback })).data;
}
export async function fetchTrackCreative(creativeId: string): Promise<TrackRow> {
  return (await api.get<TrackRow>(`/track/creatives/${creativeId}`)).data;
}

// ---------- Services SLA (ADR-020) ----------
export interface SlaHandover { key: string; label: string; hours: number; enabled: boolean }
export interface ServiceSla {
  handovers: SlaHandover[];
  rejection: { mode: 'fixed' | 'fraction'; hours: number; fraction: number };
  auto_approve: { doctor: { enabled: boolean; after_hours: number }; client: { enabled: boolean; after_hours: number } };
  remind_before_hours: number;
}
export interface ServicesSla { video: ServiceSla; stills: ServiceSla }
export async function fetchServicesSla(): Promise<ServicesSla> {
  return (await api.get<ServicesSla>('/admin/settings/services-sla')).data;
}
export async function saveServicesSla(body: ServicesSla): Promise<ServicesSla> {
  return (await api.put<ServicesSla>('/admin/settings/services-sla', body)).data;
}
export async function runSlaWorker(): Promise<{ auto_doctor: number; auto_client: number; reminded: number; overdue: number }> {
  return (await api.post('/admin/sla/run')).data;
}

// ---------- Who approves what (ADR-022) ----------
export interface ApproverCandidate { id: string; display_name: string; kind: 'staff' | 'contact'; designations: string[] }
export interface ApproverRouting {
  role: string; label: string; kind?: 'work' | 'review' | 'setting'; group?: string; named: string[]; named_names: string[];
  inherited_from: 'creative' | 'clinic' | 'nobody'; effective_names: string[]; candidates: ApproverCandidate[];
}
// Who takes the clinic's meetings, by topic (Aziz 2026-10-01).
export interface MeetingRouting {
  topics: { key: string; label: string; hint: string; chain: string[]; is_default: boolean; takes_it: string;
    lines: { entry: string; label: string; names: string[]; free: boolean[] }[] }[];
  options: { value: string; label: string; kind: 'seat' | 'person' }[];
}
export async function fetchMeetingRouting(clientId: string): Promise<MeetingRouting> {
  return (await api.get(`/clients/${clientId}/meeting-routing`)).data;
}
export async function saveMeetingRouting(clientId: string, routing: Record<string, string[]>): Promise<MeetingRouting> {
  return (await api.put(`/clients/${clientId}/meeting-routing`, { routing })).data;
}
export async function fetchClientRouting(clientId: string): Promise<ApproverRouting[]> {
  return (await api.get<ApproverRouting[]>(`/clients/${clientId}/approver-routing`)).data;
}
export async function saveClientRouting(clientId: string, routing: Record<string, string[] | boolean | string>): Promise<ApproverRouting[]> {
  return (await api.put<ApproverRouting[]>(`/clients/${clientId}/approver-routing`, { routing })).data;
}
export async function fetchCreativeRouting(creativeId: string): Promise<ApproverRouting[]> {
  return (await api.get<ApproverRouting[]>(`/creatives/${creativeId}/approver-routing`)).data;
}
export async function saveCreativeRouting(creativeId: string, routing: Record<string, string[]>): Promise<ApproverRouting[]> {
  return (await api.put<ApproverRouting[]>(`/creatives/${creativeId}/approver-routing`, { routing })).data;
}

// ---------- Client locations (migration 041) ----------
export interface LocationPhoto {
  id: string; location_id: string; filename: string; content_type?: string | null; width?: number | null; height?: number | null;
  caption?: string | null; sort_order: number; url?: string | null; thumbnail_url?: string | null; uploaded_by?: string | null; created_at: string;
}
export interface ClientLocation {
  id: string; client_id: string; kind: 'outside' | 'inside'; name: string; note?: string | null; sort_order: number; active: boolean;
  photos: LocationPhoto[]; created_at: string;
}
export async function fetchClientLocations(clientId: string): Promise<ClientLocation[]> {
  return (await api.get<ClientLocation[]>(`/clients/${clientId}/locations`)).data;
}
export async function createClientLocation(clientId: string, body: { kind?: 'outside' | 'inside'; name?: string; note?: string }): Promise<ClientLocation> {
  return (await api.post<ClientLocation>(`/clients/${clientId}/locations`, body)).data;
}
export async function patchClientLocation(clientId: string, locationId: string, body: { name?: string; note?: string; sort_order?: number; active?: boolean }): Promise<ClientLocation> {
  return (await api.patch<ClientLocation>(`/clients/${clientId}/locations/${locationId}`, body)).data;
}
export async function deleteClientLocation(clientId: string, locationId: string): Promise<void> {
  await api.delete(`/clients/${clientId}/locations/${locationId}`);
}
export async function getLocationPhotoUploadUrl(clientId: string, locationId: string, body: { filename: string; content_type: string; size_bytes: number }): Promise<{ photo_id: string; upload_url: string; thumbnail_upload_url: string }> {
  return (await api.post<{ photo_id: string; upload_url: string; thumbnail_upload_url: string }>(`/clients/${clientId}/locations/${locationId}/photos/upload-url`, body)).data;
}
export async function finalizeLocationPhoto(clientId: string, locationId: string, photoId: string, body: { width?: number; height?: number; has_thumbnail: boolean; caption?: string }): Promise<LocationPhoto> {
  return (await api.post<LocationPhoto>(`/clients/${clientId}/locations/${locationId}/photos/${photoId}/finalize`, body)).data;
}
export async function patchLocationPhoto(clientId: string, locationId: string, photoId: string, body: { caption?: string; sort_order?: number }): Promise<LocationPhoto> {
  return (await api.patch<LocationPhoto>(`/clients/${clientId}/locations/${locationId}/photos/${photoId}`, body)).data;
}
export async function deleteLocationPhoto(clientId: string, locationId: string, photoId: string): Promise<void> {
  await api.delete(`/clients/${clientId}/locations/${locationId}/photos/${photoId}`);
}

// ---------- Pre-production board and on-set feed (migration 042) ----------
export interface BoardShot { id?: string | null; what: string; who?: string | null; location_id?: string | null; done_at?: string | null; done_by?: string | null; done_by_name?: string | null }
export interface BoardLocation { id: string; kind: string; name: string; note?: string | null; active: boolean; photos: LocationPhoto[] }
export interface Board {
  creative_id: string; code: string; title: string; client_id: string; creative_type: string;
  unlocked: boolean; locked_reason?: string | null; can_edit: boolean; can_tick: boolean;
  location_ids: string[]; locations: BoardLocation[]; shots: BoardShot[]; shots_done: number;
  talent_name?: string | null; talent_notes?: string | null; props_notes?: string | null;
  doc?: { path: string; name: string; content_type?: string; size?: number; url?: string } | null;
  reference_urls: string[]; reference_count: number;
  shoot?: { id: string; title: string; shoot_at: string; status: string; location?: string | null; crew_names: string[] } | null;
  filmed_at?: string | null; updated_at?: string | null; updated_by_name?: string | null;
}
export async function fetchBoard(creativeId: string): Promise<Board> {
  return (await api.get<Board>(`/creatives/${creativeId}/board`)).data;
}
export async function patchBoard(creativeId: string, body: { location_ids?: string[]; shots?: { id?: string; what: string; who?: string; location_id?: string }[]; talent_notes?: string; props_notes?: string; doc?: { path: string; name: string; content_type: string; size: number } | null }): Promise<Board> {
  return (await api.patch<Board>(`/creatives/${creativeId}/board`, body)).data;
}
export async function tickShot(creativeId: string, shotId: string, done: boolean): Promise<Board> {
  return (await api.post<Board>(`/creatives/${creativeId}/board/shots/${shotId}/tick`, { done })).data;
}
export async function fetchShootBoards(shootId: string): Promise<Board[]> {
  return (await api.get<Board[]>(`/shoots/${shootId}/boards`)).data;
}
export async function updateMyProfile(body: { phone?: string | null }): Promise<{ phone?: string | null }> {
  return (await api.patch<{ phone?: string | null }>('/users/me/profile', body)).data;
}

/** The art director's decision on a submitted rough cut or colouring. */
export async function decidePhase(taskId: string, decision: 'approved' | 'rejected', notes?: string, mentions: string[] = []): Promise<{ task: Task; stage: string; notified: number }> {
  return (await api.post<{ task: Task; stage: string; notified: number }>(`/tasks/${taskId}/decide`, { decision, notes, mentions })).data;
}

/** Correct your own message. The server allows it for a short window after posting. */
export async function editComment(id: string, body: string, mentions: string[] = []): Promise<Comment> {
  return (await api.patch<Comment>(`/comments/${id}`, { body, mentions })).data;
}
/** Take a message back. The author, or a manager, may do this. */
export async function deleteComment(id: string): Promise<void> {
  await api.delete(`/comments/${id}`);
}

/** Declare an edit phase unnecessary on this piece, or bring it back. */
export async function skipPhase(creativeId: string, phase: 'rough' | 'colour' | 'final', skip: boolean): Promise<{ skipped_phases: string[] }> {
  return (await api.post(`/creatives/${creativeId}/phases/skip`, { phase, skip })).data;
}

/** Park a piece that is waiting on the world. A date is optional; a reason is not. */
export async function deferCreative(id: string, reason: string, until?: string, mentions: string[] = []): Promise<{ deferred_until: string | null }> {
  return (await api.post(`/creatives/${id}/defer`, { reason, until: until || null, mentions })).data;
}
/** The world caught up: put it back in the flow. */
export async function resumeCreative(id: string): Promise<void> {
  await api.delete(`/creatives/${id}/defer`);
}
